import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import type { Theme } from '@emotion/react'

import { InfoIcon } from '@segment/matcha/components/Icons/Common'

import Button from '@segment/matcha/components/Button'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'
import Divider from '@segment/matcha/components/Divider'
import Paper from '@segment/matcha/components/Paper'
import Tooltip from '@segment/matcha/components/Tooltip'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import SignupButton from '@segment/matcha/recipes/SignupButton'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'
import type { PageConfig } from '@segment/contexts/page'

type PricingCards = {
  title: string
  excerpt: string
  button: JSX.Element
  price: string
  benefitsHeading: string
  benefits: (string | (string | JSX.Element)[])[]
}

const getCards = (config: PageConfig, theme: Theme): PricingCards[] => [
  {
    title: `Free`,
    excerpt: `Start sending customer data to your favorite marketing and analytics tools.`,
    button: (
      <StyledSignupButton size="medium" variant="outlined">
        Create a free account
      </StyledSignupButton>
    ),
    price: `$0/month`,
    benefitsHeading: `What you can do:`,
    benefits: [
      `Includes 1,000 visitors/mo`,
      `500,000/mo Reverse ETL Records`,
      `2 sources`,
      `${process.env.INTEGRATION_COUNT}+ Integrations`,
      `1 data warehouse destination`,
    ],
  },
  {
    title: `Team`,
    excerpt: `Unify your customer data and collect every touch point with full access to Connections.`,
    button: (
      <StyledSignupButton variant="filled" size="medium" additionalParams={{ plan: `team-monthly` }}>
        Try for free
      </StyledSignupButton>
    ),
    price: `Starts at $120/month`,
    benefitsHeading: `All of Free plus:`,
    benefits: [
      [
        `Includes 10,000 visitors/mo`,
        <Tooltip label="+$10/mo for 1,000 monthly visitors" key="info-icon">
          <InfoIcon color={theme.palette.twilio.gray[40]} />
        </Tooltip>,
      ],
      `1,000,000 Reverse ETL Records`,
      `Unlimited Sources`,
      `Public API Access`,
    ],
  },
  {
    title: `Business`,
    excerpt: `Solve complex business problems with standardized data and deploy the industry’s #1 CDP for market share per IDC, 2022.`,
    button: (
      <StyledButton size="medium" color="primary" variant="outlined" href={getAbsoluteUrl(`/demo/`, config)}>
        Get a demo
      </StyledButton>
    ),
    price: `Custom Pricing`,
    benefitsHeading: `All of Team plus:`,
    benefits: [
      `Custom Volume`,
      `Single View of the Customer`,
      `Data Governance`,
      `Advanced Roles & Permissions`,
      `Personalized Customer Experiences`,
      `HIPAA-eligibility (BAA Required)`,
      [
        `Regional Segment (EU or US)`,
        <Tooltip label="Segment offers regional infrastructure in both Europe and the United States" key="info-icon">
          <InfoIcon color={theme.palette.twilio.gray[40]} />
        </Tooltip>,
      ],
    ],
  },
]

const PlansCards: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const theme = useTheme()

  return (
    <Wrapper>
      <StyledSectionBase variant="centered">
        <Section.Contents>
          <ColumnTrack columns={1} overrides={{ lg: 3 }}>
            {getCards(pageConfig, theme).map((card, index) => {
              return (
                <StyledPaper key={index} elevation={3}>
                  <Typography tag="h2" variant="h2">
                    {card.title}
                  </Typography>
                  <Typography variant="pMedium">{card.excerpt}</Typography>
                  {card.button}
                  <Price variant="pLarge">{card.price}</Price>
                  <Divider />
                  <ListHeading variant="pMedium">{card.benefitsHeading}</ListHeading>
                  <List>
                    {card.benefits.map((benefit, index) => (
                      <StyledListItem key={`benefit-${index}`}>
                        <Benefit variant="pMedium">
                          {Array.isArray(benefit) ? benefit.map((item) => item) : benefit}
                        </Benefit>
                      </StyledListItem>
                    ))}
                  </List>
                </StyledPaper>
              )
            })}
          </ColumnTrack>
        </Section.Contents>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1320px;

  ${Section.Wrapper} {
    padding-top: 0;

    ${({ theme }) => theme.breakpoints.down(`md`)} {
      row-gap: 40px;
    }
  }
`

const StyledPaper = styled(Paper)`
  display: grid;
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
  padding: 0px 24px 24px;
  grid-template-rows: 10% 25% repeat(4, min-content) 1fr;
  max-width: 327px;
  justify-self: center;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 8px 32px 32px;
    grid-template-rows: 10% 22.5% repeat(4, min-content) 1fr;
    max-width: 352px;
  }
`

const StyledSignupButton = styled(SignupButton)`
  width: 100%;
  height: 56px;
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 56px;
`

const Price = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  font-weight: 600;
`

const ListHeading = styled(Typography)`
  font-weight: 600;
`

const List = styled.ul`
  margin-bottom: 24px;
  padding-left: 24px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-bottom: 0;
  }
`

const StyledListItem = styled.li`
  list-style-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='9' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.045.667a.91.91 0 0 1 .025 1.305L5.031 8.126a.95.95 0 0 1-1.356 0L.93 5.33a.91.91 0 0 1 .025-1.305.954.954 0 0 1 1.331.025l2.067 2.106L9.714.692a.954.954 0 0 1 1.33-.025Z' fill='%23121C2D'/%3e%3c/svg%3e ");

  font-weight: 400;
  padding: 0 0 8px 8px;
`

const Benefit = styled(Typography)`
  margin: 0;
  display: flex;
  gap: 8px;
`
export default PlansCards
